import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ScheduledChannelSnapshot } from 'dashboard/interfaces/scheduled-channel-snapshot.interface';
import { ScheduledChannelSnapshotsService } from 'dashboard/services/scheduled-channel-snapshots.service';
import { filter, switchMap } from 'rxjs';
import { DataGridDateCellRendererComponent } from 'shared/components/data-grid/data-grid-date-cell-renderer/data-grid-date-cell-renderer.component';
import { DataGridDefaultCellRendererComponent } from 'shared/components/data-grid/data-grid-default-cell-renderer/data-grid-default-cell-renderer.component';
import { DataGridComponent } from 'shared/components/data-grid/data-grid.component';
import { DefaultDataGridActionCellRendererComponent } from 'shared/components/data-grid/default-data-grid-action-cell-renderer/default-data-grid-action-cell-renderer.component';
import { DataGridColumnDefinition } from 'shared/interfaces/data-grid.interface';
import { ModalService, ToastService } from 'shared/services';
import { SharedModule } from 'shared/shared.module';
import { ScheduledChannelSnapshotModalComponent } from './modals/scheduled-channel-snapshot-modal.component';
import { ScheduledChannelSnapshotsEnabledCellRendererComponent } from './table-renderers/scheduled-channel-snapshots-enabled-cell-renderer/scheduled-channel-snapshots-enabled-cell-renderer.component';
import { ScheduledChannelSnapshotsFrequencyCellRendererComponent } from './table-renderers/scheduled-channel-snapshots-frequency-cell-renderer/scheduled-channel-snapshots-frequency-cell-renderer.component';
import { ScheduledChannelSnapshotsRunStatusCellRendererComponent } from './table-renderers/scheduled-channel-snapshots-run-status-cell-renderer/scheduled-channel-snapshots-run-status-cell-renderer.component';

@Component({
    selector: 'app-scheduled-channel-snapshots-widget',
    templateUrl: './scheduled-channel-snapshots-widget.component.html',
    styleUrls: ['./scheduled-channel-snapshots-widget.component.scss'],
    imports: [TranslateModule, SharedModule, DataGridComponent],
})
export class ScheduledChannelSnapshotsWidgetComponent implements OnInit {
    public columns?: DataGridColumnDefinition[];

    public data: ScheduledChannelSnapshot[] = [];

    constructor(
        private destroyRef: DestroyRef,
        private modalService: ModalService,
        private toastService: ToastService,
        private translateService: TranslateService,
        private scheduledChannelSnapshotsService: ScheduledChannelSnapshotsService,
    ) {}

    public ngOnInit(): void {
        this.generateColumnDefinitions();
        this.getWidgetData();
    }

    private generateColumnDefinitions() {
        this.columns = [
            {
                headerLabel: 'dashboard.scheduledChannelSnapshots.table.supplierName',
                dataKey: 'supplierName',
                customCellRenderer: DataGridDefaultCellRendererComponent,
            },
            {
                headerLabel: 'dashboard.scheduledChannelSnapshots.table.numberOfChannels',
                dataKey: 'channelIds.length',
                customCellRenderer: DataGridDefaultCellRendererComponent,
            },
            {
                headerLabel: 'dashboard.scheduledChannelSnapshots.table.schedule',
                dataKey: 'schedule.frequency',
                customCellRenderer: ScheduledChannelSnapshotsFrequencyCellRendererComponent,
            },
            {
                headerLabel: 'dashboard.scheduledChannelSnapshots.table.lastRunTime',
                dataKey: 'lastRunTime',
                customCellRenderer: DataGridDateCellRendererComponent,
                customCellRendererData: {
                    format: 'yyyy-MM-dd HH:mm:ss',
                },
                sortable: true,
            },
            {
                headerLabel: 'dashboard.scheduledChannelSnapshots.table.lastRunStatus',
                dataKey: 'lastRunStatus',
                customCellRenderer: ScheduledChannelSnapshotsRunStatusCellRendererComponent,
                sortable: true,
            },
            {
                headerLabel: 'dashboard.scheduledChannelSnapshots.table.nextRunTime',
                dataKey: 'nextRunTime',
                customCellRenderer: DataGridDateCellRendererComponent,
                customCellRendererData: {
                    format: 'yyyy-MM-dd HH:mm:ss',
                },
                sortable: true,
            },
            {
                headerLabel: 'dashboard.scheduledChannelSnapshots.table.enabled',
                dataKey: 'enabled',
                customCellRenderer: ScheduledChannelSnapshotsEnabledCellRendererComponent,
            },
            {
                headerLabel: '',
                customCellRenderer: DefaultDataGridActionCellRendererComponent,
                customCellRendererData: [
                    {
                        label: 'dynamicForm.table.actions.edit',
                        icon: 'pen-to-square',
                        clickHandler: this.openScheduledChannelSnapshotModal.bind(this),
                    },
                    {
                        label: 'dynamicForm.table.actions.delete',
                        icon: 'trash',
                        clickHandler: this.handleDelete.bind(this),
                    },
                    {
                        label: 'dynamicForm.table.actions.run',
                        icon: 'play',
                        clickHandler: this.handleRun.bind(this),
                    },
                ],
            },
        ];
    }

    private getWidgetData() {
        this.scheduledChannelSnapshotsService
            .getScheduledChannelSnapshots()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data: ScheduledChannelSnapshot[]) => {
                this.data = data;
            });
    }

    public handleDelete(data: ScheduledChannelSnapshot) {
        this.modalService
            .openDeleteConfirmationModal(
                undefined,
                undefined,
                undefined,
                data.supplierName,
                this.translateService.instant('dashboard.scheduledChannelSnapshots.delete.label'),
            )
            .onClose.pipe(
                takeUntilDestroyed(this.destroyRef),
                filter(Boolean),
                switchMap(() => {
                    return this.scheduledChannelSnapshotsService.deleteScheduledChannelSnapshot(data.id);
                }),
            )
            .subscribe(() => {
                this.toastService.showToast('dashboard.scheduledChannelSnapshots.toast.deleteSuccess', 'success');
                this.getWidgetData();
            });
    }

    public handleRun(data: ScheduledChannelSnapshot) {
        this.scheduledChannelSnapshotsService
            .runScheduledSnapshot(data.id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.toastService.showToast('dashboard.scheduledChannelSnapshots.toast.runSuccess', 'success');
                this.getWidgetData();
            });
    }

    public openScheduledChannelSnapshotModal(report?: any) {
        const modalData = { ...(report ?? {}), type: 'center' };

        this.modalService
            .openModal(ScheduledChannelSnapshotModalComponent, modalData)
            .onClose.pipe(takeUntilDestroyed(this.destroyRef), filter(Boolean))
            .subscribe(() => {
                this.getWidgetData();
            });
    }
}
